import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/home';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="pt-16 md:pt-18 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">您访问的文章可能已被移除或链接有误</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <main>
      <Navbar />
      <div className="pt-16 md:pt-18">
        <article className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors duration-200 cursor-pointer">{t('breadcrumb.home')}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/category/tian-douhua" className="hover:text-primary-600 transition-colors duration-200 cursor-pointer">{t('breadcrumb.category')}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors duration-200 cursor-pointer">{article.categoryName}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-600 truncate max-w-[200px]">{article.title}</span>
            </nav>

            <div className="mb-8">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 mb-4">
                {article.categoryName}
              </span>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
                {article.excerpt}
              </p>
              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-400">
                <span className="flex items-center gap-1"><i className="ri-tools-line"></i>{t('recipes.difficulty')}：{article.difficulty}</span>
                <span className="flex items-center gap-1"><i className="ri-time-line"></i>{t('recipes.time')}：{article.prepTime}</span>
                <span className="flex items-center gap-1"><i className="ri-user-line"></i>{t('recipes.servings')}：{article.servings}</span>
              </div>
            </div>

            <div className="relative w-full h-64 md:h-80 lg:h-96 rounded-lg overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5 flex items-center gap-2">
                <i className="ri-shopping-basket-line text-primary-500"></i>
                {t('article.ingredients')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {article.ingredients.map((ingredient, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-background-100/70">
                    <div className="w-2 h-2 rounded-full bg-primary-400 flex-shrink-0"></div>
                    <span className="text-sm text-foreground-700">{ingredient}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5 flex items-center gap-2">
                <i className="ri-list-check text-primary-500"></i>
                {t('article.steps')}
              </h2>
              <div className="flex flex-col gap-4">
                {article.steps.map((step, idx) => (
                  <div key={idx} className="flex gap-4 p-4 rounded-lg bg-background-100/70">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold flex-shrink-0">
                      {idx + 1}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed pt-1">{step}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-10 p-5 md:p-6 rounded-lg bg-accent-100/50 border border-accent-200/50">
              <h2 className="font-heading text-lg font-bold text-accent-800 mb-2 flex items-center gap-2">
                <i className="ri-lightbulb-flash-line"></i>
                {t('article.tips')}
              </h2>
              <p className="text-sm text-accent-700 leading-relaxed">{article.tips}</p>
            </section>

            <div className="flex justify-center mb-12">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-2 px-6 py-2.5 border border-primary-500 text-primary-600 rounded-full text-sm font-medium hover:bg-primary-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                {t('article.back')}
              </Link>
            </div>

            {relatedArticles.length > 0 && (
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6 text-center">
                  {t('article.related')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="group rounded-lg overflow-hidden bg-background-100/70 hover:bg-background-100 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative w-full h-40 overflow-hidden">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200">
                          {rel.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </div>
      <Footer />
    </main>
  );
}